import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronRight, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import eventBus from "../../utils/eventBus";
import EffectCard from "../EffectCard";
import { useDispatch, useSelector } from "react-redux";
import {
  addVideoEffects,
  selectHasMoreVideoEffects,
  selectVideoEffects,
  selectVideoEffectsLastVisible,
  setHasMoreVideoEffects,
  setVideoEffects,
  setVideoEffectsLastVisible,
  selectShowVideoEffectsModal,
  setShowVideoEffectsModal,
} from "../../store/effectsSlice";
import { selectUserGlobalData } from "../../store/dreamworldSlice";
import dreamWorldProvider from "../../DreamWorldProvider";
import { Effect } from "../../types/effects";
import InfiniteScroll from "react-infinite-scroll-component";
import Section from "../ui/Section";

const EffectsSection: React.FC = () => {
  const [displayCount, setDisplayCount] = useState(10);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const videoEffects = useSelector(selectVideoEffects);
  const lastVisible = useSelector(selectVideoEffectsLastVisible);
  const hasMore = useSelector(selectHasMoreVideoEffects);
  const showAll = useSelector(selectShowVideoEffectsModal);

const userGlobalData = useSelector(selectUserGlobalData);
  useEffect(() => {
    const fetchEffects = async () => {
      if (videoEffects.length === 0 && userGlobalData?.user?.uid) {
        const { effects, lastVisible: newLastVisible } =
          await dreamWorldProvider.getEffects("video", userGlobalData.user.uid);
        dispatch(setVideoEffects(effects));
        dispatch(setVideoEffectsLastVisible(newLastVisible));
        if (effects.length < 20) {
          dispatch(setHasMoreVideoEffects(false));
        }
      }
    };

    fetchEffects();
  }, [dispatch, videoEffects.length, userGlobalData]);

  // Calculate display count based on screen size for desktop grid
  useEffect(() => {
    if (showAll) {
      eventBus.emit("fullscreen:enter");
    } else {
      eventBus.emit("fullscreen:exit");
    }
  }, [showAll]);
  useEffect(() => {
    const updateDisplayCount = () => {
      const width = window.innerWidth;
      let columnsPerRow = 4; // Default for sm screens

      if (width >= 1024) {
        // lg breakpoint
        columnsPerRow = 8;
      } else if (width >= 768) {
        // md breakpoint
        columnsPerRow = 5;
      } else if (width >= 640) {
        // sm breakpoint
        columnsPerRow = 4;
      }

      // For mobile, keep the original 10 items
      // For desktop, show 2 full rows
      const newDisplayCount = width < 640 ? 10 : columnsPerRow * 2;
      setDisplayCount(newDisplayCount);
    };

    updateDisplayCount();
    window.addEventListener("resize", updateDisplayCount);
    return () => window.removeEventListener("resize", updateDisplayCount);
  }, []);

  const handleEffectClick = (effect: Effect) => {
    navigate("/create", { state: { effect } });
  };

  const fetchMoreEffects = async () => {
    if (hasMore && userGlobalData?.user?.uid) {
      const { effects, lastVisible: newLastVisible } =
        await dreamWorldProvider.getEffects(
          "video",
          userGlobalData.user.uid,
          lastVisible
        );
      dispatch(addVideoEffects(effects));
      dispatch(setVideoEffectsLastVisible(newLastVisible));
      if (effects.length < 20) {
        dispatch(setHasMoreVideoEffects(false));
      }
    }
  };

  const displayedEffects = videoEffects.slice(0, displayCount);

  return (
    <>
      <Section
        title="Effects"
        onSeeAll={() => dispatch(setShowVideoEffectsModal(true))}
      >
        {/* Mobile: Horizontal scroll */}
        <div className="sm:hidden">
          <div className="overflow-x-auto pb-4">
            <div className="flex gap-1.5 min-w-max">
              {displayedEffects.map((effect, index) => (
                <div
                  key={effect.id}
                  style={{
                    marginLeft: index === 0 ? "8px" : undefined,
                    marginRight:
                      index === displayedEffects.length - 1 ? "8px" : undefined,
                  }}
                >
                  <EffectCard
                    effect={effect}
                    onClick={() => handleEffectClick(effect)}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Desktop: Wrapped grid */}
        <div className="hidden sm:grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-8 gap-1 px-2">
          {displayedEffects.map((effect) => (
            <EffectCard
              key={effect.id}
              effect={effect}
              onClick={() => handleEffectClick(effect)}
            />
          ))}
        </div>
      </Section>

      <AnimatePresence>
        {showAll && (
          <motion.div
            initial={{ opacity: 0, y: "100%" }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: "100%" }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 bg-gray-900/90 backdrop-blur-xl z-50 overflow-y-auto"
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
              <div className="flex items-center justify-between mb-12">
                <h2 className="text-3xl font-bold text-white">All Effects</h2>
                <motion.button
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  onClick={() => dispatch(setShowVideoEffectsModal(false))}
                  className="p-2 rounded-full hover:bg-white/10"
                >
                  <X className="text-white" />
                </motion.button>
              </div>
              <InfiniteScroll
                dataLength={videoEffects.length}
                next={fetchMoreEffects}
                hasMore={hasMore}
                loader={<h4>Loading...</h4>}
                endMessage={
                  <p style={{ textAlign: "center" }}>
                    <b>Yay! You have seen it all</b>
                  </p>
                }
                className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6"
              >
                {videoEffects.map((effect) => (
                  <EffectCard
                    key={effect.id}
                    effect={effect}
                    onClick={() => handleEffectClick(effect)}
                  />
                ))}
              </InfiniteScroll>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default EffectsSection;
